<script setup lang="ts">
import { onMounted, onUnmounted } from "vue";
import WrButton from "~/components/global/WrButton.vue";
import WrIcon from "~/components/global/WrIcon.vue";

const colorMode = useColorMode();
const isLight = computed(() => colorMode.value !== "dark");

interface ComparisonCard {
  title: string;
  description: string;
  svgIcon?: string;
}

const emit = defineEmits<{ "cta-clicked": [] }>();

const props = withDefaults(
  defineProps<{
    heading: string;
    subtitle: string;
    leftTitle: string;
    leftCards: ComparisonCard[];
    rightTitle: string;
    rightCards: ComparisonCard[];
    ctaText?: string;
    ctaLink?: string | null;
  }>(),
  {
    ctaText: "Get Started",
    ctaLink: null,
  }
);

const sectionRef = ref<HTMLElement | null>(null);
const visible = ref(false);
let observer: IntersectionObserver | null = null;

onMounted(() => {
  if (sectionRef.value) {
    observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          visible.value = true;
          observer?.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    observer.observe(sectionRef.value);
  }
});

onUnmounted(() => {
  observer?.disconnect();
});
</script>

<template>
  <section
    ref="sectionRef"
    class="mt-24 lg:mt-[100px] mb-16 lg:mb-[100px]"
    aria-label="Comparison Section"
  >
    <h2
      class="text-[28px] lg:text-[48px] font-semibold leading-tight text-center font-avant-demi"
      style="
        background: linear-gradient(92deg, #12dbff -3.57%, #ba55fe 80.72%);
        background-clip: text;
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
      "
    >
      {{ heading }}
    </h2>
    <p
      class="text-[16px] lg:text-[18px] font-normal tracking-[0.36px] mt-4 px-4 text-center max-w-[720px] mx-auto font-avant lg:opacity-70"
      :class="isLight ? 'text-black' : 'text-white'"
    >
      {{ subtitle }}
    </p>

    <div class="mt-10 lg:mt-16 relative max-w-[964px] mx-auto px-4">
      <div class="hidden lg:block absolute left-1/2 top-0 bottom-0 w-px -translate-x-1/2 z-10">
        <div class="comp-divider w-full h-full"></div>
      </div>

      <div class="comp-container">
        <!-- Left Header -->
        <div class="comp-header comp-header-left">
          <div class="comp-dot comp-dot-left"></div>
          <h3 class="comp-title">{{ leftTitle }}</h3>
        </div>

        <!-- Left Cards -->
        <div
          v-for="(card, index) in leftCards"
          :key="'left-' + index"
          class="comp-card comp-card-left hover-left"
          :class="visible ? 'anim-left' : ''"
          :style="{ animationDelay: `${index * 100}ms` }"
        >
          <div class="comp-icon icon-left">
            <WrIcon v-if="card.svgIcon" :name="card.svgIcon" class="size-5 text-[#12DBFF]" />
          </div>
          <div class="comp-text">
            <h4 class="comp-label" :class="isLight ? 'text-[#000]' : 'text-white'">{{ card.title }}</h4>
            <p class="comp-desc" :class="isLight ? 'text-[#666]' : 'text-[#BCB2C3]'">{{ card.description }}</p>
          </div>
        </div>

        <!-- Right Header -->
        <div class="comp-header comp-header-right">
          <div class="comp-dot comp-dot-right"></div>
          <h3 class="comp-title">{{ rightTitle }}</h3>
        </div>

        <!-- Right Cards -->
        <div
          v-for="(card, index) in rightCards"
          :key="'right-' + index"
          class="comp-card comp-card-right hover-right"
          :class="visible ? 'anim-right' : ''"
          :style="{ animationDelay: `${(leftCards.length + index) * 100}ms` }"
        >
          <div class="comp-icon icon-right">
            <WrIcon v-if="card.svgIcon" :name="card.svgIcon" class="size-5 text-[#BA55FE]" />
          </div>
          <div class="comp-text">
            <h4 class="comp-label" :class="isLight ? 'text-[#000]' : 'text-white'">{{ card.title }}</h4>
            <p class="comp-desc" :class="isLight ? 'text-[#666]' : 'text-[#BCB2C3]'">{{ card.description }}</p>
          </div>
        </div>
      </div>
    </div>

    <div class="flex justify-center mt-8 lg:mt-12">
      <WrButton
        :link="ctaLink || undefined"
        @click="!ctaLink ? emit('cta-clicked') : undefined"
        container-class="h-[40px] lg:h-[48px]"
        append-class="text-sm lg:font-normal px-3 lg:px-5"
      >
        {{ ctaText }}
      </WrButton>
    </div>
  </section>
</template>

<style scoped>
/* ====== LAYOUT: CSS Grid ====== */
/* Mobile: 1 column / Desktop: 2 columns — auto-flow dense for proper ordering */
.comp-container {
  display: grid;
  grid-template-columns: 1fr;
  grid-auto-flow: dense;
}

@media (min-width: 1024px) {
  .comp-container {
    grid-template-columns: 1fr 1fr;
  }
}

/* Grid placement: mobile = stacked, desktop = interleaved rows */
.comp-header-left {
  grid-column: 1 / -1;
}

.comp-header-right {
  grid-column: 1 / -1;
}

@media (min-width: 1024px) {
  .comp-header-left {
    grid-column: 1 / 2;
    grid-row: 1;
  }

  .comp-header-right {
    grid-column: 2 / 3;
    grid-row: 1;
  }
}

/* ====== DIVIDER ====== */
.comp-divider {
  background: linear-gradient(
    180deg,
    transparent 0%,
    rgba(18, 219, 255, 0.2) 20%,
    rgba(186, 85, 254, 0.2) 80%,
    transparent 100%
  );
}

/* ====== HEADER ====== */
.comp-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 18px;
}

/* Mobile: top header rounded top, bottom header rounded bottom */
.comp-header-left {
  border-radius: 12px 12px 0 0;
}

.comp-header-right {
  border-radius: 0 0 12px 12px;
}

@media (min-width: 1024px) {
  .comp-header-left {
    border-radius: 16px 0 0 0;
    padding: 16px 20px;
  }

  .comp-header-right {
    border-radius: 0 16px 0 0;
    padding: 16px 20px;
  }
}

.comp-title {
  font-family: "ITC Avant Garde Gothic Demi", "ITC Avant Garde Gothic", "AvantGarde", sans-serif;
  font-size: 16px;
  font-weight: 600;
  line-height: normal;
  color: #000;
}

:root.my-app-dark .comp-title {
  color: #fff;
}

@media (min-width: 1024px) {
  .comp-title {
    font-size: 20px;
  }
}

/* ====== CARD ====== */
.comp-card {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 14px 18px;
  min-height: 72px;
  box-sizing: border-box;
  transition: all 0.3s ease;
}

@media (min-width: 1024px) {
  .comp-card {
    padding: 16px 20px;
  }
}

/* Mobile borders */
.comp-card {
  border-left: 1px solid rgba(255, 255, 255, 0.04);
  border-right: 1px solid rgba(255, 255, 255, 0.04);
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
}

/* Desktop borders */
@media (min-width: 1024px) {
  .comp-card {
    border: none;
  }

  .comp-card-left {
    grid-column: 1;
    border-left: 1px solid rgba(18, 219, 255, 0.06);
    border-bottom: 1px solid rgba(18, 219, 255, 0.06);
  }

  .comp-card-right {
    grid-column: 2;
    border-right: 1px solid rgba(186, 85, 254, 0.06);
    border-bottom: 1px solid rgba(186, 85, 254, 0.06);
  }
}

/* Backgrounds */
.comp-card-left {
  background: rgba(18, 219, 255, 0.02);
}

.comp-card-right {
  background: rgba(186, 85, 254, 0.02);
}

/* Last row rounded corners on mobile */
@media (max-width: 1023px) {
  .comp-card:last-child {
    border-bottom-left-radius: 12px;
    border-bottom-right-radius: 12px;
  }
}

/* ====== HOVER (desktop only) ====== */
@media (min-width: 1024px) {
  .hover-left:hover {
    background: rgba(18, 219, 255, 0.06);
    border-left-color: rgba(18, 219, 255, 0.3);
    transform: translateX(2px);
  }

  .hover-right:hover {
    background: rgba(186, 85, 254, 0.06);
    border-right-color: rgba(186, 85, 254, 0.3);
    transform: translateX(-2px);
  }
}

/* ====== ICON ====== */
.comp-icon {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  transition: all 0.3s ease;
}

.icon-left {
  background: rgba(18, 219, 255, 0.08);
  border: 1px solid rgba(18, 219, 255, 0.2);
}

.icon-right {
  background: rgba(186, 85, 254, 0.08);
  border: 1px solid rgba(186, 85, 254, 0.2);
}

@media (min-width: 1024px) {
  .hover-left:hover .icon-left {
    background: rgba(18, 219, 255, 0.15);
    border-color: rgba(18, 219, 255, 0.4);
    box-shadow: 0 0 20px rgba(18, 219, 255, 0.2);
    transform: scale(1.05);
  }

  .hover-right:hover .icon-right {
    background: rgba(186, 85, 254, 0.15);
    border-color: rgba(186, 85, 254, 0.4);
    box-shadow: 0 0 20px rgba(186, 85, 254, 0.2);
    transform: scale(1.05);
  }
}

/* ====== TEXT ====== */
.comp-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.comp-label {
  font-family: "ITC Avant Garde Gothic Demi", "ITC Avant Garde Gothic", "AvantGarde", sans-serif;
  font-size: 15px;
  font-weight: 600;
  line-height: normal;
}

.comp-desc {
  font-family: "ITC Avant Garde Gothic", "AvantGarde", sans-serif;
  font-size: 13px;
  line-height: 17px;
}

/* ====== DOTS ====== */
.comp-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}

.comp-dot-left {
  background: #12dbff;
  box-shadow: 0 0 12px rgba(18, 219, 255, 0.6), 0 0 24px rgba(18, 219, 255, 0.25);
  animation: dot-pulse-l 2s ease-in-out infinite;
}

.comp-dot-right {
  background: #ba55fe;
  box-shadow: 0 0 12px rgba(186, 85, 254, 0.6), 0 0 24px rgba(186, 85, 254, 0.25);
  animation: dot-pulse-r 2s ease-in-out infinite;
}

@keyframes dot-pulse-l {
  0%, 100% { box-shadow: 0 0 12px rgba(18, 219, 255, 0.6), 0 0 24px rgba(18, 219, 255, 0.25); transform: scale(1); }
  50% { box-shadow: 0 0 16px rgba(18, 219, 255, 0.8), 0 0 32px rgba(18, 219, 255, 0.35); transform: scale(1.15); }
}

@keyframes dot-pulse-r {
  0%, 100% { box-shadow: 0 0 12px rgba(186, 85, 254, 0.6), 0 0 24px rgba(186, 85, 254, 0.25); transform: scale(1); }
  50% { box-shadow: 0 0 16px rgba(186, 85, 254, 0.8), 0 0 32px rgba(186, 85, 254, 0.35); transform: scale(1.15); }
}

/* ====== ENTRANCE ====== */
.anim-left {
  animation: in-l 0.5s ease-out both;
}

.anim-right {
  animation: in-r 0.5s ease-out both;
}

@keyframes in-l {
  from { opacity: 0; transform: translateX(-30px); }
  to { opacity: 1; transform: translateX(0); }
}

@keyframes in-r {
  from { opacity: 0; transform: translateX(30px); }
  to { opacity: 1; transform: translateX(0); }
}
</style>